import { AfterViewInit, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { NAVIGATIONS, URLS } from '../../../../data/navigation/navigation.data';
import { AI_COST_ABOUT } from '../../../../data/tool/tool-about.data';
import { AI_TOKEN_METER_MORE_TOOL } from '../../../../data/product/ai-token-meter-features.data';
import { AnalyticsService } from '../../../../service/analytics/analytics.service';
import { AiCostCardChange } from '../../../../component/ai-cost-widget/ai-cost-widget.component';
import { AiPrices, AiPricesService } from '../../../../service/ai/ai-prices.service';
import {
  AI_COST_DEFAULTS, AI_COST_FIELDS, AiCostRow, AiCostValues, aiCostScenario, aiMoney, aiPlatformIcon, clampAiCost, priceAiCost,
} from '../../../../util/ai-cost';
import { SeoService } from '../../../../service/seo/seo.service';

/** the table's columns that sort (owner 2026-10-19) */
type SortKey = 'name' | 'platform' | 'month' | 'day' | 'request' | 'budget';

/**
 * /tools/ai-cost-calculator (owner 2026-10-19): what an app costs a month on every model of the daily table (GET
 * /ai/prices), cheapest first. Made for people who do not think in tokens (owner, same evening): a ready-made use
 * fills the numbers in, the questions ask for requests a day and words, repeated text, days and a budget wait
 * under More Options, and the card gives the bill on the cheapest model or the one picked. The inputs live in the url
 * (?s=chatbot&r=1000&in=2000&out=500&c=0&d=30&b=0&m=<model id>; in/out stay tokens) so an estimate can be shared.
 * Everything is computed in the browser.
 */
@Component( {
  selector: 'app-ai-cost-page',
  templateUrl: './ai-cost-page.component.html',
  styleUrls: [
    './ai-cost-page.component.scss',
    './ai-cost-table.scss',
    '../../tool-pages/wallpaper-page/wallpaper-page.component.scss',
    '../../tool-pages/tools-page/tools-page.component.scss',
    '../../plugin-pages/plugins-page/plugins-page.component.scss',
  ],
} )
export class AiCostPageComponent implements OnInit, AfterViewInit, OnDestroy {

  URLS = URLS;
  data: AiPrices | null = null;
  platformKeys: string[] = [];
  platform = 'all';
  query = '';
  rows: AiCostRow[] = [];
  /** cheapest first by default; ?sort=platform, ?sort=-month for the other way */
  sortKey: SortKey = 'month';
  sortDesc = false;

  /** the card's numbers (component/ai-cost-widget), the use and the model; null = the cheapest */
  values: AiCostValues = { ...AI_COST_DEFAULTS };
  scenario = aiCostScenario( 'chatbot' );
  modelId: string | null = null;

  pageSections: { key: string; label: string }[] = [
    { key: 'compare', label: 'Compare Every Model' },
    { key: 'more', label: 'More AI Tools' },
    { key: 'about', label: 'How It Works' },
    { key: 'reviews', label: 'Reviews' },
  ];
  activeSection = 'top';
  /** More AI Tools: AI Token Meter */
  readonly aiTokenMeter = AI_TOKEN_METER_MORE_TOOL;
  readonly aboutLead = AI_COST_ABOUT.lead;
  readonly aboutParagraphs = AI_COST_ABOUT.paragraphs;
  readonly faq = AI_COST_ABOUT.faq;

  private sectionObserver?: IntersectionObserver;
  private subs: Subscription[] = [];
  private logged = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private prices: AiPricesService,
    private analyticsService: AnalyticsService,
    private seoService: SeoService,
    private zone: NgZone,
  ) {
  }

  ngOnInit(): void {
    this.seoService.setMetaData( {
      title: NAVIGATIONS.aiCostCalculator.title,
      description: NAVIGATIONS.aiCostCalculator.description,
      keywords: NAVIGATIONS.aiCostCalculator.keywords,
      robots: [ 'index', 'follow', 'max-image-preview:large', 'max-snippet:-1' ],
    } );
    this.subs.push( this.route.queryParams.subscribe( q => {
      const values = { ...this.values };
      AI_COST_FIELDS.forEach( f => {
        const n = Number( q[ f.param ] );
        if ( q[ f.param ] !== undefined && isFinite( n ) ) { values[ f.key ] = clampAiCost( f, n ); }
      } );
      this.values = values;
      if ( q.s ) { this.scenario = aiCostScenario( q.s ); } else if ( q.r !== undefined ) { this.scenario = aiCostScenario( 'custom' ); }
      this.modelId = typeof q.m === 'string' && q.m ? q.m : null;
      this.platform = typeof q.platform === 'string' && q.platform ? q.platform : 'all';
      this.query = typeof q.q === 'string' ? q.q : '';
      const sort = typeof q.sort === 'string' ? q.sort : '';
      const sortKey = sort.replace( /^-/, '' ) as SortKey;
      this.sortKey = [ 'name', 'platform', 'month', 'day', 'request', 'budget' ].indexOf( sortKey ) >= 0 ? sortKey : 'month';
      this.sortDesc = sort.startsWith( '-' );
      this.build();
    } ) );
    this.subs.push( this.prices.prices$.subscribe( data => {
      this.data = data;
      this.platformKeys = Object.keys( data.platforms );
      if ( this.platform !== 'all' && this.platformKeys.indexOf( this.platform ) < 0 ) { this.platform = 'all'; }
      this.build();
    } ) );
  }

  ngAfterViewInit(): void {
    if ( typeof IntersectionObserver === 'undefined' ) { return; }
    this.sectionObserver = new IntersectionObserver( entries => {
      for ( const entry of entries ) {
        if ( entry.isIntersecting ) {
          const sec = ( entry.target as HTMLElement ).dataset[ 'section' ];
          if ( sec && sec !== this.activeSection ) { this.zone.run( () => ( this.activeSection = sec ) ); }
        }
      }
    }, { rootMargin: '-45% 0px -50% 0px', threshold: 0 } );
    setTimeout( () => document.querySelectorAll( '[data-section]' ).forEach( el => this.sectionObserver!.observe( el ) ) );
  }

  ngOnDestroy(): void {
    this.subs.forEach( s => s.unsubscribe() );
    this.sectionObserver?.disconnect();
  }

  // ---------------------------------------------------------------- the inputs

  /** the card moved: keep its numbers, use and model, and write them to the url */
  onCard( change: AiCostCardChange ): void {
    this.values = change.values;
    this.scenario = aiCostScenario( change.scenarioKey );
    this.modelId = change.modelId;
    this.apply( change.source );
  }

  /** a row of the table becomes the card's model */
  pickModel( row: AiCostRow ): void {
    this.modelId = row.model.id;
    this.apply( 'model' );
  }

  /** the card's row, for the table's highlight: the picked model, else the cheapest */
  get chosenId(): string | null {
    return this.modelId || this.allRows[ 0 ]?.model.id || null;
  }

  setPlatform( key: string ): void {
    this.platform = key;
    this.apply( 'platform' );
  }

  /** a header: sort by it, a second click turns the order round */
  sortBy( key: SortKey ): void {
    if ( this.sortKey === key ) {
      this.sortDesc = !this.sortDesc;
    } else {
      this.sortKey = key;
      this.sortDesc = false;
    }
    this.apply( 'sort' );
  }

  setQuery( value: string ): void {
    this.query = value;
    this.apply( 'search' );
  }

  platformIcon( key: string ): string {
    return aiPlatformIcon( key );
  }

  platformLabel( key: string ): string {
    return this.data?.platforms[ key ]?.label || key;
  }

  money( value: number ): string {
    return aiMoney( value );
  }

  count( value: number ): string {
    return Math.round( value ).toLocaleString( 'en-US' );
  }

  price( value: number | null ): string {
    return AiPricesService.price( value );
  }

  private apply( source: 'value' | 'platform' | 'search' | 'scenario' | 'model' | 'sort' ): void {
    this.build();
    const params: { [ key: string ]: string | null } = {
      platform: this.platform === 'all' ? null : this.platform,
      q: this.query.trim() || null,
      s: this.scenario.key,
      m: this.modelId,
      sort: this.sortKey === 'month' && !this.sortDesc ? null : ( this.sortDesc ? '-' : '' ) + this.sortKey,
    };
    AI_COST_FIELDS.forEach( f => ( params[ f.param ] = String( this.values[ f.key ] ) ) );
    this.router.navigate( [], { relativeTo: this.route, queryParams: params, queryParamsHandling: 'merge', replaceUrl: true } );
    if ( !this.logged || source === 'platform' ) {
      this.logged = true;
      this.analyticsService.logEvent( 'AI Cost Calculated', { ...this.values, platform: this.platform, scenario: this.scenario.key } );
    }
  }

  // ---------------------------------------------------------------- the bill

  /** every model priced, cheapest first, before the platform and search filters */
  private allRows: AiCostRow[] = [];

  private build(): void {
    if ( !this.data ) { this.rows = []; this.allRows = []; return; }
    const q = this.query.trim().toLowerCase();
    const all = priceAiCost( this.data.models, this.values );
    this.allRows = all;
    const rows = all.filter( r => ( this.platform === 'all' || r.model.platform === this.platform )
      && ( !q || `${ r.model.name } ${ r.model.vendor } ${ r.model.id } ${ this.platformLabel( r.model.platform ) }`.toLowerCase().indexOf( q ) >= 0 ) );
    if ( this.sortKey !== 'month' || this.sortDesc ) {
      const order = Object.keys( this.data.platforms );
      const dir = this.sortDesc ? -1 : 1;
      const value = ( r: AiCostRow ): number => this.sortKey === 'day' ? r.perDay : this.sortKey === 'request' ? r.perRequest
        : this.sortKey === 'budget' ? r.withinBudget : r.perMonth;
      rows.sort( ( a, b ) => {
        let c: number;
        if ( this.sortKey === 'name' ) {
          c = a.model.name.localeCompare( b.model.name, 'en', { numeric: true } );
        } else if ( this.sortKey === 'platform' ) {
          c = order.indexOf( a.model.platform ) - order.indexOf( b.model.platform ) || a.perMonth - b.perMonth;
        } else {
          c = value( a ) - value( b );
        }
        return c * dir || a.model.name.localeCompare( b.model.name );
      } );
    }
    this.rows = rows;
  }

  // ---------------------------------------------------------------- the chips

  scrollToSection( key: string ): void {
    const scroller = ( document.body.scrollHeight > document.body.clientHeight + 4 ) ? document.body : document.documentElement;
    if ( key === 'top' ) {
      this.activeSection = 'top';
      this.tweenScroll( scroller, 0 );
      return;
    }
    const el = document.getElementById( 'sec-' + key );
    if ( !el ) { return; }
    this.activeSection = key;
    let headerOffset = 84;
    const bar = document.querySelector( '.cp-chipbar' ) as HTMLElement | null;
    if ( bar ) {
      const stickyTop = parseFloat( getComputedStyle( bar ).top ) || 72;
      headerOffset = stickyTop + bar.offsetHeight + 16;
    }
    this.tweenScroll( scroller, Math.max( 0, el.getBoundingClientRect().top + scroller.scrollTop - headerOffset ) );
  }

  private tweenScroll( scroller: HTMLElement, target: number ): void {
    const startTop = scroller.scrollTop;
    const start = performance.now();
    const ease = ( t: number ) => 1 - Math.pow( 1 - t, 3 );
    const step = ( now: number ) => {
      const p = Math.min( 1, ( now - start ) / 420 );
      scroller.scrollTop = startTop + ( target - startTop ) * ease( p );
      if ( p < 1 ) { requestAnimationFrame( step ); }
    };
    requestAnimationFrame( step );
  }
}
